import { forwardRef, HTMLAttributes } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { cn } from '@/lib/cn';
import { fadeInUp } from '@/lib/motion';

type CardVariant = 'glass' | 'neumorphic' | 'solid';

interface CardProps extends Omit<HTMLMotionProps<'div'>, 'title'> {
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  animate?: boolean;
  hover?: boolean;
  glow?: 'none' | 'cyan' | 'purple' | 'blue';
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
}

interface CardContentProps extends HTMLAttributes<HTMLDivElement> {}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {}

const variantStyles: Record<CardVariant, string> = {
  glass: cn(
    'bg-white/[0.03] backdrop-blur-xl',
    'border border-white/[0.08]',
    'shadow-glass'
  ),
  neumorphic: cn(
    'bg-navy-800',
    'shadow-neu-md'
  ),
  solid: cn(
    'bg-navy-800/90',
    'border border-navy-600/50'
  ),
};

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const glowStyles = {
  none: '',
  cyan: 'hover:shadow-glow-cyan',
  purple: 'hover:shadow-glow-purple',
  blue: 'hover:shadow-glow-blue',
};

/**
 * Card - Versatile card component with multiple variants
 * 
 * Features:
 * - Glass, neumorphic, and solid variants
 * - Optional glow effect on hover
 * - Entrance animation
 * - Composable with CardHeader, CardContent, CardFooter
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'glass',
      padding = 'md',
      animate = true,
      hover = true,
      glow = 'none',
      children,
      ...props
    },
    ref
  ) => {
    const animationProps = animate
      ? {
          initial: 'hidden',
          animate: 'visible',
          variants: fadeInUp,
        }
      : {};

    return (
      <motion.div
        ref={ref}
        className={cn(
          'relative rounded-2xl',
          variantStyles[variant],
          paddingStyles[padding],
          glowStyles[glow],
          hover && 'transition-all duration-300 hover:bg-white/[0.05]',
          className
        )}
        {...animationProps}
        {...props}
      >
        {children}
      </motion.div>
    );
  }
);

Card.displayName = 'Card';

/**
 * CardHeader - Header section for Card
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, subtitle, action, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-between pb-4',
        'border-b border-white/5 mb-4',
        className
      )}
      {...props}
    >
      {(title || subtitle) && (
        <div>
          {title && (
            <h3 className="text-lg font-display font-semibold text-white">{title}</h3>
          )}
          {subtitle && (
            <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>
          )}
        </div>
      )}
      {children}
      {action && <div>{action}</div>}
    </div>
  )
);

CardHeader.displayName = 'CardHeader';

/**
 * CardContent - Content section for Card
 */
export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('', className)}
      {...props}
    />
  )
);

CardContent.displayName = 'CardContent';

/**
 * CardFooter - Footer section for Card
 */
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-end gap-3 pt-4',
        'border-t border-white/5 mt-4',
        className
      )}
      {...props}
    />
  )
);

CardFooter.displayName = 'CardFooter';

export default Card;

